import React, { useState } from 'react';
import { Settings, Trash2, Save, AlertTriangle, Camera, DollarSign, FlaskConical, Gift, Calendar, Wallet, Plus, AlertCircle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../../services/firebase';
import clubService from '../../services/clubService';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';

const ClubSettings = ({ club, onBack, onClubUpdated, onClubDeleted, isSuperAdmin = false }) => {
  const [clubName, setClubName] = useState(club.name);
  const [clubLocation, setClubLocation] = useState(club.location);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [testModeEnabled, setTestModeEnabled] = useState(club.testModeEnabled || false);

  // Registration Settings
  const [requireRegistration, setRequireRegistration] = useState(club.requireRegistration || false);
  const [registrationFormats, setRegistrationFormats] = useState(
    club.registrationFormats || [
      { id: 'tennis', label: 'Tennis Ball', price: 125 },
      { id: 'leather', label: 'Leather Ball', price: 250 }
    ]
  );

  // Waiver Settings
  const [requireWaiver, setRequireWaiver] = useState(club.requireWaiver || false);
  const [waiverText, setWaiverText] = useState(club.waiverText || '');

  // Initial Donation states
  const [leatherBallFee, setLeatherBallFee] = useState(club.initialDonationAmounts?.leather ?? 250);
  const [tennisBallFee, setTennisBallFee] = useState(club.initialDonationAmounts?.tennis ?? 125);

  // Additional Donation states
  const [additionalDonationAmount, setAdditionalDonationAmount] = useState('');
  const [additionalDonationDescription, setAdditionalDonationDescription] = useState('');
  const [applyingDonation, setApplyingDonation] = useState(false);

  // Luma Event Banner states
  const [lumaBannerEnabled, setLumaBannerEnabled] = useState(club.lumaBannerEnabled || false);
  const [lumaEventUrl, setLumaEventUrl] = useState(club.lumaEventUrl || '');
  const [lumaTitle, setLumaTitle] = useState(club.lumaTitle || '');
  const [lumaBadge, setLumaBadge] = useState(club.lumaBadge || '');
  const [lumaDescription, setLumaDescription] = useState(club.lumaDescription || '');
  const [lumaEventDate, setLumaEventDate] = useState(club.lumaEventDate || '');

  // Payment Option states
  const [zelleEmail, setZelleEmail] = useState(club.paymentDetails?.zelleEmail || '');
  const [zelleQrBase64, setZelleQrBase64] = useState(club.paymentDetails?.zelleQrUrl || null);
  const [uploadingZelleQr, setUploadingZelleQr] = useState(false);

  const [venmoLink, setVenmoLink] = useState(club.paymentDetails?.venmoLink || '');
  const [venmoQrBase64, setVenmoQrBase64] = useState(club.paymentDetails?.venmoQrUrl || null);
  const [uploadingVenmoQr, setUploadingVenmoQr] = useState(false);

  const [paypalLink, setPaypalLink] = useState(club.paymentDetails?.paypalLink || '');
  const [paypalQrBase64, setPaypalQrBase64] = useState(club.paymentDetails?.paypalQrUrl || null);
  const [uploadingPaypalQr, setUploadingPaypalQr] = useState(false);

  const [benevitySearch, setBenevitySearch] = useState(club.paymentDetails?.benevitySearch || '');

  const handleAddFormat = () => {
    setRegistrationFormats([
      ...registrationFormats,
      { id: `format-${Date.now()}`, label: 'New Format', price: 0 }
    ]);
  };

  const handleRemoveFormat = (id) => {
    setRegistrationFormats(registrationFormats.filter(f => f.id !== id));
  };

  const handleFormatChange = (id, field, value) => {
    setRegistrationFormats(formats => formats.map(f => {
      if (f.id === id) {
        return { ...f, [field]: field === 'price' ? Number(value) : value };
      }
      return f;
    }));
  };

  const handleQrUpload = async (e, method, setUrl, setUploading) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }

    setUploading(true);
    try {
      const qrRef = ref(storage, `clubs/${club.id}/payment-qr-codes/${method}`);
      await uploadBytes(qrRef, file);
      const qrUrl = await getDownloadURL(qrRef);

      setUrl(qrUrl);
      alert(`${method} QR Code uploaded successfully! Save settings to persist.`);
    } catch (error) {
      console.error(`Error uploading ${method} QR:`, error);
      alert(`Unable to upload ${method} QR. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const logoRef = ref(storage, `clubs/${club.id}/logo`);
      await uploadBytes(logoRef, file);
      const logoUrl = await getDownloadURL(logoRef);

      await clubService.updateClub(club.id, { logoUrl });
      onClubUpdated({ ...club, logoUrl });
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Unable to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateClub = async () => {
    if (!clubName.trim()) {
      alert('Please enter a club name');
      return;
    }

    if (leatherBallFee < 0 || tennisBallFee < 0) {
      alert('Initial donations cannot be negative');
      return;
    }

    setLoading(true);
    try {
      const newLeatherFee = parseFloat(leatherBallFee) || 0;
      const newTennisFee = parseFloat(tennisBallFee) || 0;
      const oldLeatherFee = club.initialDonationAmounts?.leather ?? 250;
      const oldTennisFee = club.initialDonationAmounts?.tennis ?? 125;

      console.log('🔵 Updating club settings');

      const updateData = {
        name: clubName,
        location: clubLocation,
        initialDonationAmounts: {
          leather: newLeatherFee,
          tennis: newTennisFee
        },
        lumaBannerEnabled,
        lumaEventUrl,
        lumaTitle,
        lumaBadge,
        lumaDescription,
        lumaEventDate,
        testModeEnabled: testModeEnabled,
        requireRegistration,
        registrationFormats,
        requireWaiver,
        waiverText,
        paymentDetails: {
          zelleEmail: zelleEmail || null,
          zelleQrUrl: zelleQrBase64 || null,
          venmoLink: venmoLink || null,
          venmoQrUrl: venmoQrBase64 || null,
          paypalLink: paypalLink || null,
          paypalQrUrl: paypalQrBase64 || null,
          benevitySearch: benevitySearch || null,
        },
        updatedAt: new Date().toISOString()
      };

      // Update club document
      await clubService.updateClub(club.id, updateData);

      console.log('✅ Club settings updated in Firestore');

      // AUTO-UPDATE ALL EXISTING INITIAL DONATIONS
      // Only run if fees changed
      if (oldLeatherFee !== newLeatherFee || oldTennisFee !== newTennisFee) {
        console.log('💰 Fees changed, checking for expenses to auto-update...');

        let leatherUpdated = 0;
        let tennisUpdated = 0;

        // 1. Fetch expenses for this club
        const expensesRef = collection(db, 'expenses');
        const expQ = query(expensesRef, where('clubId', '==', club.id), where('isInitialDonation', '==', true));
        const expSnap = await getDocs(expQ);
        const regFeeExpenses = [];
        expSnap.forEach(doc => regFeeExpenses.push({ id: doc.id, ...doc.data() }));

        // 2. Fetch teams for this club (to know ball type for each player)
        const teamsRef = collection(db, 'teams');
        const teamsQ = query(teamsRef, where('clubId', '==', club.id));
        const teamsSnap = await getDocs(teamsQ);
        const teams = [];
        teamsSnap.forEach(doc => teams.push({ id: doc.id, ...doc.data() }));

        console.log('📊 Found', regFeeExpenses.length, 'initial donations to check');

        for (const feeExpense of regFeeExpenses) {
          const playerId = feeExpense.splitAmong?.[0];
          const playerEmail = feeExpense.playerEmail?.toLowerCase();

          // Find what teams this player is on
          let playsLeather = false;
          let playsOnlyTennis = false;
          let foundPlayer = false;

          teams.forEach(team => {
            if (team.players) {
              const playerInTeam = team.players.find(p =>
                p.id === playerId ||
                p.userId === playerId ||
                (playerEmail && p.email?.toLowerCase() === playerEmail)
              );
              if (playerInTeam) {
                foundPlayer = true;
                if (team.ballType === 'tennis') {
                  playsOnlyTennis = true;
                } else {
                  playsLeather = true;
                }
              }
            }
          });

          // Determine the correct new fee
          let newFee;
          if (playsLeather) {
            newFee = newLeatherFee;
          } else if (foundPlayer && playsOnlyTennis && !playsLeather) {
            newFee = newTennisFee;
          } else {
            // Default to leather if we couldn't determine
            newFee = newLeatherFee;
          }

          const oldFee = feeExpense.amount;

          // Only update if fee has changed
          if (oldFee !== newFee) {
            console.log('  ✅ Updating fee:', feeExpense.id, 'from $', oldFee, 'to $', newFee);

            const feeRef = doc(db, 'expenses', feeExpense.id);
            await updateDoc(feeRef, {
              amount: newFee,
              amountPerPlayer: newFee,
              updatedAt: new Date().toISOString(),
              updateReason: 'Auto-updated when club fee settings changed'
            });

            if (newFee === newLeatherFee) {
              leatherUpdated++;
            } else {
              tennisUpdated++;
            }
          }
        }
        console.log('✅ Auto-updated fees: Leather:', leatherUpdated, ', Tennis:', tennisUpdated);
      }

      alert('Club settings updated successfully!');

      onClubUpdated({
        ...club,
        ...updateData
      });
      onBack();
    } catch (error) {
      console.error('Error updating club:', error);
      alert('Unable to update club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async () => {
    setLoading(true);
    try {
      // Delete all teams associated with the club
      for (const teamId of club.teams || []) {
        try {
          await window.storage.delete(`team:${teamId}`);
        } catch (err) {
          console.error('Error deleting team:', teamId, err);
        }
      }

      // Delete all tournaments
      for (const tournamentId of club.tournaments || []) {
        try {
          await window.storage.delete(`tournament:${tournamentId}`);
        } catch (err) {
          console.error('Error deleting tournament:', tournamentId, err);
        }
      }

      // Delete the club
      const result = await clubService.deleteClub(club.id);

      if (result.success) {
        alert('Club deleted successfully!');
        onClubDeleted(club.id);
      } else {
        alert('Unable to delete club. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting club:', error);
      alert('Unable to delete club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAdditionalDonation = async () => {
    if (!additionalDonationAmount || parseFloat(additionalDonationAmount) <= 0) {
      alert('Please enter a valid amount greater than 0');
      return;
    }

    if (!additionalDonationDescription.trim()) {
      alert('Please enter a description for this donation (e.g., "Field Maintenance")');
      return;
    }

    setApplyingDonation(true);
    try {
      // 1. Fetch all players in the club
      const teamsRef = collection(db, 'teams');
      const q = query(teamsRef, where('clubId', '==', club.id));
      const querySnapshot = await getDocs(q);

      const allPlayerIds = new Set();
      let playerCount = 0;

      querySnapshot.forEach((doc) => {
        const team = doc.data();
        if (team.players && Array.isArray(team.players)) {
          team.players.forEach(player => {
            if (player.id) {
              allPlayerIds.add(player.id);
            }
          });
        }
      });

      playerCount = allPlayerIds.size;

      if (playerCount === 0) {
        alert('No players found in this club to apply the donation to.');
        setApplyingDonation(false);
        return;
      }

      const amountPerPlayer = parseFloat(additionalDonationAmount);
      const totalExpectedRevenue = amountPerPlayer * playerCount;

      const confirmMessage = `Apply Additional Donation?\n\n` +
        `• Amount: $${amountPerPlayer.toFixed(2)} per player\n` +
        `• Description: "${additionalDonationDescription}"\n` +
        `• Target: ${playerCount} players\n` +
        `• Total Expected: $${totalExpectedRevenue.toFixed(2)}\n\n` +
        `This will add a dues record for every player in the club. Proceed?`;

      if (!confirm(confirmMessage)) {
        setApplyingDonation(false);
        return;
      }

      // 2. Create the shared expense document
      const expensesRef = collection(db, 'expenses');
      await addDoc(expensesRef, {
        description: additionalDonationDescription.trim(),
        amount: totalExpectedRevenue,
        amountPerPlayer: amountPerPlayer,
        gameId: null,
        gameName: null,
        tournamentId: null,
        tournamentName: null,
        clubId: club.id,
        splitAmong: Array.from(allPlayerIds), // The list of all player IDs
        receiptURL: null,
        createdAt: new Date().toISOString(),
        createdBy: club.adminEmail || 'admin', // Fallback
        createdByName: 'Club Admin',

        // Critical flags for "Receivables" calculation
        isRegistrationFee: true, // Treating it like a reg fee ensures it shows as a defined due
        isInitialDonation: false, // It's not the detailed sign-up fee
        type: 'Additional Donation',

        approvalStatus: 'approved',
        approvedBy: 'system',
        approvedAt: new Date().toISOString()
      });

      alert(`✅ Successfully applied donation to ${playerCount} players!`);

      // Reset form
      setAdditionalDonationAmount('');
      setAdditionalDonationDescription('');

    } catch (error) {
      console.error('Error applying additional donation:', error);
      alert('Failed to apply donation: ' + error.message);
    } finally {
      setApplyingDonation(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack} className="text-gray-600 hover:text-gray-900 mb-6">
        ← Back to Club
      </button>

      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="text-gray-600" size={32} />
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Club Settings</h2>
            <p className="text-gray-600">{club.name}</p>
          </div>
        </div>

        {/* Club Logo Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Club Logo</h3>
          <div className="flex items-start gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                {club.logoUrl ? (
                  <img src={club.logoUrl} alt="Club logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-gray-400">{club.name?.charAt(0)}</span>
                )}
              </div>
              <label className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 shadow-lg">
                <Camera size={16} />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </label>
            </div>
            <div className="flex-1">
              <p className="text-gray-600 text-sm">Upload a logo for your club. This will appear on reports and communications.</p>
              <p className="text-gray-400 text-xs mt-1">Recommended: Square image, at least 200x200 pixels</p>
              {uploadingLogo && (
                <p className="text-blue-600 text-sm mt-2 flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Uploading...
                </p>
              )}
            </div>
          </div>
        </div>

        <hr className="my-8" />

        {/* Club Details Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Club Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Club Name
              </label>
              <input
                type="text"
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter club name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={clubLocation}
                onChange={(e) => setClubLocation(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter location"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Club Code:</strong> {club.code}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                The club code cannot be changed. Players use this code to join the club.
              </p>
            </div>
          </div>
        </div>

        <hr className="my-8" />

        {/* Test Mode Section - Super Admin Only */}
        {isSuperAdmin && (
          <>
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FlaskConical className="text-purple-600" size={24} />
                Test Mode
              </h3>
              <div className={`border-2 rounded-lg p-4 ${testModeEnabled ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={testModeEnabled}
                    onChange={(e) => setTestModeEnabled(e.target.checked)}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-semibold text-gray-900">Enable Test Mode</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      When enabled, club admins can impersonate any user to test the player experience
                    </p>
                  </div>
                </label>
                {testModeEnabled && (
                  <div className="mt-3 p-3 bg-purple-100 rounded-lg">
                    <p className="text-sm text-purple-800">
                      ⚠️ <strong>Test Mode is active.</strong> Admins will see a "View as" dropdown to impersonate users.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <hr className="my-8" />
          </>
        )}

        {/* Onboarding & Registration Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="text-blue-600" size={24} />
            Registration & Onboarding
          </h3>

          <div className="space-y-6">
            {/* Registration Settings */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">Mandatory Registration Form</h4>
                  <p className="text-sm text-gray-600">Require players to fill out a registration form and pay an initial donation fee before accessing the club.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={requireRegistration}
                    onChange={(e) => setRequireRegistration(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {requireRegistration && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-semibold text-sm text-gray-800">Registration Formats & Fees</h5>
                    <button
                      onClick={handleAddFormat}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium bg-blue-50 px-2 py-1 rounded"
                    >
                      <Plus size={14} />
                      Add Format
                    </button>
                  </div>
                  
                  {registrationFormats.length === 0 ? (
                    <div className="text-center py-4 bg-white rounded border border-gray-200">
                      <p className="text-gray-500 text-xs">No formats defined.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {registrationFormats.map((format) => (
                        <div key={format.id} className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200 shadow-sm">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={format.label}
                              onChange={(e) => handleFormatChange(format.id, 'label', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Format Name"
                            />
                          </div>
                          <div className="w-24 relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                            <input
                              type="number"
                              min="0"
                              value={format.price}
                              onChange={(e) => handleFormatChange(format.id, 'price', e.target.value)}
                              className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveFormat(format.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Waiver Settings */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">Mandatory Liability Waiver</h4>
                  <p className="text-sm text-gray-600">Require players to read and sign a liability waiver before accessing the club.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={requireWaiver}
                    onChange={(e) => setRequireWaiver(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {requireWaiver && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Waiver Text
                  </label>
                  <textarea
                    value={waiverText}
                    onChange={(e) => setWaiverText(e.target.value)}
                    rows="8"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                    placeholder="Enter your liability waiver terms here..."
                  ></textarea>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className="my-8" />

        {/* Payment Methods Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Wallet className="text-blue-600" size={24} />
            Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Configure how players can securely pay club dues. Leave a method blank to hide it from the "Pay Now" screen.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Zelle Settings */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
              <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-purple-200 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">Z</span>
                Zelle Configuration
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Zelle Email / Phone Number</label>
                  <input
                    type="text"
                    value={zelleEmail}
                    onChange={(e) => setZelleEmail(e.target.value)}
                    placeholder="e.g. club@gmail.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Optional QR Code</label>
                  <div className="flex items-center gap-4">
                    {zelleQrBase64 ? (
                      <div className="w-16 h-16 bg-white border border-gray-200 rounded p-1 object-contain flex-shrink-0">
                        <img src={zelleQrBase64} alt="Zelle QR" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center flex-shrink-0 text-xs text-gray-400">
                        No QR
                      </div>
                    )}
                    <label className="cursor-pointer bg-white border border-gray-300 shadow-sm px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
                      <Camera size={14} />
                      {uploadingZelleQr ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleQrUpload(e, 'zelle', setZelleQrBase64, setUploadingZelleQr)}
                        className="hidden"
                        disabled={uploadingZelleQr}
                      />
                    </label>
                    {zelleQrBase64 && (
                      <button onClick={() => setZelleQrBase64(null)} className="text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Venmo Settings */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-5">
              <h4 className="font-semibold text-sky-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-sky-200 text-sky-700 rounded-full flex items-center justify-center text-xs font-bold">V</span>
                Venmo Configuration
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Venmo Username or Handle (@)</label>
                  <input
                    type="text"
                    value={venmoLink}
                    onChange={(e) => setVenmoLink(e.target.value)}
                    placeholder="e.g. @Club-Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Optional QR Code</label>
                  <div className="flex items-center gap-4">
                    {venmoQrBase64 ? (
                      <div className="w-16 h-16 bg-white border border-gray-200 rounded p-1 object-contain flex-shrink-0">
                        <img src={venmoQrBase64} alt="Venmo QR" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center flex-shrink-0 text-xs text-gray-400">
                        No QR
                      </div>
                    )}
                    <label className="cursor-pointer bg-white border border-gray-300 shadow-sm px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                      <Camera size={14} />
                      {uploadingVenmoQr ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleQrUpload(e, 'venmo', setVenmoQrBase64, setUploadingVenmoQr)}
                        className="hidden"
                        disabled={uploadingVenmoQr}
                      />
                    </label>
                    {venmoQrBase64 && (
                      <button onClick={() => setVenmoQrBase64(null)} className="text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PayPal Settings */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-200 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">P</span>
                PayPal Configuration
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">PayPal Direct Link URL</label>
                  <input
                    type="url"
                    value={paypalLink}
                    onChange={(e) => setPaypalLink(e.target.value)}
                    placeholder="e.g. https://paypal.me/clubname"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Optional QR Code</label>
                  <div className="flex items-center gap-4">
                    {paypalQrBase64 ? (
                      <div className="w-16 h-16 bg-white border border-gray-200 rounded p-1 object-contain flex-shrink-0">
                        <img src={paypalQrBase64} alt="PayPal QR" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center flex-shrink-0 text-xs text-gray-400">
                        No QR
                      </div>
                    )}
                    <label className="cursor-pointer bg-white border border-gray-300 shadow-sm px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                      <Camera size={14} />
                      {uploadingPaypalQr ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleQrUpload(e, 'paypal', setPaypalQrBase64, setUploadingPaypalQr)}
                        className="hidden"
                        disabled={uploadingPaypalQr}
                      />
                    </label>
                    {paypalQrBase64 && (
                      <button onClick={() => setPaypalQrBase64(null)} className="text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Benevity Settings (Hidden for now) */}
            {/* 
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
              <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-200 text-orange-700 rounded-full flex items-center justify-center text-xs font-bold">B</span>
                Benevity Gateway
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Charity Search Query</label>
                  <input
                    type="text"
                    value={benevitySearch}
                    onChange={(e) => setBenevitySearch(e.target.value)}
                    placeholder="e.g. Seattle United Yorkas Club"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Players will be instructed to search for this exact string within their employer's matching donation portal.
                  </p>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>

        <hr className="my-8 border-gray-200" />

        {/* Initial Donations Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="text-green-600" size={24} />
            Initial Donations
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Set the Initial Donation amounts for your club. These fees are automatically applied when players are added to teams.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Leather Ball Fee */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                🏏 Leather Ball
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={leatherBallFee}
                  onChange={(e) => setLeatherBallFee(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Fee for players joining leather ball teams</p>
            </div>

            {/* Tennis Ball Fee */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                🎾 Tennis Ball Only
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tennisBallFee}
                  onChange={(e) => setTennisBallFee(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Fee for players joining tennis ball only teams</p>
            </div>
          </div>
        </div>

        <hr className="my-6 border-gray-100" />

        {/* Bulk Additional Donation Section */}
        <div className="mb-0">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Gift className="text-purple-600" size={18} />
            Bulk Additional Donation
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Apply a one-time fee to ALL players in the club (e.g., maintenance fee, special event).
          </p>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={additionalDonationAmount}
                  onChange={(e) => setAdditionalDonationAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={additionalDonationDescription}
                  onChange={(e) => setAdditionalDonationDescription(e.target.value)}
                  placeholder="e.g. Annual Field Maintenance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <button
              onClick={handleApplyAdditionalDonation}
              disabled={applyingDonation || !additionalDonationAmount || !additionalDonationDescription}
              className="w-full py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {applyingDonation ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  Applying to all players...
                </>
              ) : (
                <>
                  Apply to All Players
                </>
              )}
            </button>
          </div>
        </div>

        <hr className="my-8" />

        {/* Luma Event Integration Section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="text-indigo-600" size={24} />
            Luma Event Integration
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Embed a "Register for Event" button for your Luma events directly on the player dashboard.
          </p>

          <div className={`border-2 rounded-lg p-4 ${lumaBannerEnabled ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={lumaBannerEnabled}
                onChange={(e) => setLumaBannerEnabled(e.target.checked)}
                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="font-semibold text-gray-900">Enable Luma Event Banner</span>
            </label>

            {lumaBannerEnabled && (
              <>
                <div className="pl-8">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Luma Event URL
                  </label>
                  <input
                    type="url"
                    value={lumaEventUrl}
                    onChange={(e) => setLumaEventUrl(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. https://luma.com/event/evt-..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste the full URL of your Luma event page.
                  </p>
                </div>

                <div className="pl-8 mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Title
                    </label>
                    <input
                      type="text"
                      value={lumaTitle}
                      onChange={(e) => setLumaTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Annual Club Gala"
                    />
                    <p className="text-xs text-gray-500 mt-1">Defaults to "Upcoming Club Event" if empty</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Badge Text (Optional)
                    </label>
                    <input
                      type="text"
                      value={lumaBadge}
                      onChange={(e) => setLumaBadge(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Featured Event"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Short Description (Optional)
                    </label>
                    <input
                      type="text"
                      value={lumaDescription}
                      onChange={(e) => setLumaDescription(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Click to register now!"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Date & Time (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={lumaEventDate}
                      onChange={(e) => setLumaEventDate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">If set, a countdown timer will appear on the banner.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <hr className="my-8" />

        {/* Save Button */}
        <div className="mb-8">
          <button
            onClick={handleUpdateClub}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save All Settings
              </>
            )}
          </button>
        </div>

        <hr className="my-8" />

        {/* Delete Club Section */}
        <div>
          <h3 className="text-xl font-bold text-red-900 mb-4">Danger Zone</h3>

          {!showDeleteConfirm ? (
            <div className="border-2 border-red-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Delete This Club</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Once you delete a club, there is no going back. This will permanently delete:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 mb-4">
                    <li>The club and all its settings</li>
                    <li>All teams ({club.teams?.length || 0} teams)</li>
                    <li>All tournaments ({club.tournaments?.length || 0} tournaments)</li>
                    <li>All games and expenses associated with this club</li>
                    <li>All events and player data</li>
                  </ul>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                  >
                    <Trash2 size={18} />
                    Delete Club
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-red-500 bg-red-50 rounded-lg p-6">
              <div className="flex items-start gap-4 mb-4">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-red-900 mb-2">
                    Are you absolutely sure?
                  </h4>
                  <p className="text-sm text-red-800 mb-3">
                    This action <strong>cannot be undone</strong>. This will permanently delete the club{' '}
                    <strong className="font-semibold">{club.name}</strong> and all associated data.
                  </p>
                  <div className="bg-white border border-red-300 rounded p-3 mb-4">
                    <p className="text-sm text-gray-700 mb-2">
                      Please type <strong className="font-mono bg-gray-100 px-2 py-1 rounded">{club.name}</strong> to confirm:
                    </p>
                    <input
                      type="text"
                      id="delete-confirm-input"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Type club name to confirm"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const input = document.getElementById('delete-confirm-input');
                        if (input.value === club.name) {
                          handleDeleteClub();
                        } else {
                          alert('Club name does not match. Please type the exact club name to confirm deletion.');
                        }
                      }}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={18} />
                          Yes, Delete Club Permanently
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubSettings;